import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  LayoutGrid,
  FileText,
  LogOut,
  ChevronDown,
  Loader2,
  Shield,
  User,
  CreditCard,
  BarChart3,
  Bot,
  Brain,
  Compass,
  Store,
  FolderLock,
  Globe,
  ShieldAlert,
  Save,
  AlertTriangle,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import "./Sidebar.css";

const Sidebar = ({ user, apiClient }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loggingOut, setLoggingOut] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const performLogout = async () => {
    setLoggingOut(true);
    try {
      await apiClient.post("/auth/logout");
    } catch (error) {
      console.error("Logout failed:", error);
    } finally {
      try { localStorage.removeItem("session_token"); } catch {}
      // Clear any user-scoped wizard / draft data so a fresh login starts clean.
      try {
        Object.keys(localStorage).forEach((k) => {
          if (k.startsWith("ipo_assessment_wizard_v1_")) localStorage.removeItem(k);
        });
      } catch {}
      setLoggingOut(false);
      setConfirmOpen(false);
      navigate("/login");
    }
  };

  // Bridge into any open editor: trigger window-level "save before logout"
  // hooks so individual modules (DRHP editor, BV inputs, Assessment, etc.)
  // get a chance to flush in-flight changes. Each module that wants to
  // persist work just listens to `ipolabs:save-before-logout` on window.
  const saveAndLogout = async () => {
    setSaving(true);
    try {
      // Fire a custom event with a Promise resolver so listeners can save
      // asynchronously before we proceed.
      const savePromises = [];
      const ev = new CustomEvent("ipolabs:save-before-logout", {
        detail: { register: (p) => savePromises.push(p) },
      });
      window.dispatchEvent(ev);
      if (savePromises.length) {
        await Promise.allSettled(savePromises);
        toast.success(`Saved ${savePromises.length} item${savePromises.length > 1 ? "s" : ""} before logout`);
      }
    } catch (e) {
      console.warn("[logout] save hook errored:", e);
    } finally {
      setSaving(false);
      await performLogout();
    }
  };

  const handleLogout = () => setConfirmOpen(true);

  const isActive = (itemId) => {
    const path = location.pathname;
    switch (itemId) {
      case "dashboard-new": return path.startsWith("/dashboard-new") && !location.search.includes("screen=reseller");
      case "global-reseller": return path.startsWith("/dashboard-new") && location.search.includes("screen=reseller");
      case "ipo-navigator": return path.startsWith("/ipo-navigator");
      case "advisor-marketplace": return path.startsWith("/advisor-marketplace");
      case "document-vault": return path.startsWith("/document-vault");
      case "drhp-workspace": return path.startsWith("/drhp-workspace");
      case "oracle": return path.startsWith("/oracle");
      case "admin": return path.startsWith("/admin");
      case "account": return path.startsWith("/account");
      case "payments": return path.startsWith("/payments");
      case "deep-market-research": return path.startsWith("/deep-market-research") || path.startsWith("/market-analytics");
      case "drhp-intelligence":     return path.startsWith("/drhp-intelligence");
      case "fraud-risk-analytics":  return path.startsWith("/fraud-risk-analytics");
      default: return false;
    }
  };

  const isAdmin = user?.is_admin || false;
  const loginRole = user?.login_role || user?.user_type || "existing_user";
  const isEmployee = loginRole === "employee";

  // STRICT 3-email allow-list for Account Details (platform owners only).
  // MUST mirror backend `ACCOUNT_DETAILS_ADMIN_EMAILS` in shared.py.
  const ACCOUNT_OWNER_EMAILS = [
    "founders.ipolabs@gmail.com",
    "ronraj2312@gmail.com",
    "neeraj@emergent.sh",
  ];
  const isAccountOwner = ACCOUNT_OWNER_EMAILS.includes((user?.email || "").toLowerCase());

  // ── Grouped navigation (Remote-style sections) ──────────────────────
  const sections = [
    {
      id: "workspace",
      label: null,
      items: [
        { id: "dashboard-new", label: "Dashboard", icon: LayoutGrid, path: "/dashboard-new" },
        { id: "ipo-navigator", label: "IPO Navigator", icon: Compass, path: "/ipo-navigator" },
        { id: "advisor-marketplace", label: "Advisor Marketplace", icon: Store, path: "/advisor-marketplace" },
        { id: "document-vault", label: "Document Vault", icon: FolderLock, path: "/document-vault" },
        { id: "drhp-workspace", label: "DRHP Workspace", icon: FileText, path: "/drhp-workspace?phase=DATA_COLLECTION" },
        { id: "oracle", label: "Oracle {IPO.GPT}", icon: Bot, path: "/oracle" },
        { id: "global-reseller", label: "Global Reseller Program", icon: Globe, path: "/dashboard-new?screen=reseller" },
      ],
    },
    {
      id: "deep-tech",
      label: "Deep-Tech Modules (Coming Soon)",
      items: [
        { id: "drhp-intelligence",     label: "DRHP Intelligence",        icon: Brain,     path: "/drhp-intelligence" },
        { id: "fraud-risk-analytics",  label: "Fraud & Risk Analytics",   icon: ShieldAlert, path: "/fraud-risk-analytics" },
        { id: "deep-market-research",  label: "Deep Market Research",     icon: BarChart3, path: "/deep-market-research" },
      ],
    },
    {
      id: "company",
      label: "Company",
      items: [
        ...(isAdmin
          ? [
              { id: "admin", label: "Admin Center", icon: Shield, path: "/admin" },
              ...(isAccountOwner
                ? [{ id: "account", label: "Account Details", icon: User, path: "/account" }]
                : []),
            ]
          : isEmployee
          ? [
              { id: "admin", label: "Admin Center", icon: Shield, path: "/admin", disabled: true },
            ]
          : []),
        { id: "payments", label: "Payment Gateway", icon: CreditCard, path: "/payments" },
      ],
    },
  ];

  const renderNavItem = (item) => {
    const active = isActive(item.id);
    const Icon = item.icon;

    const base = "ipo-side-item";
    const stateCls = item.disabled ? "disabled" : active ? "active" : "";

    const cls = `${base} ${stateCls}`.trim();

    const inner = (
      <>
        <Icon className="ipo-side-ico" strokeWidth={2} />
        <span className="flex-1 truncate">{item.label}</span>
        {item.disabled && (
          <span className="ipo-side-soon">
            Soon
          </span>
        )}
      </>
    );

    if (item.disabled) {
      return (
        <button key={item.id} type="button" disabled className={cls} data-testid={`nav-${item.id}`}>
          {inner}
        </button>
      );
    }
    if (item.onClick) {
      return (
        <button key={item.id} type="button" onClick={item.onClick} className={cls} data-testid={`nav-${item.id}`}>
          {inner}
        </button>
      );
    }
    return (
      <Link key={item.id} to={item.path} className={cls} data-testid={`nav-${item.id}`}>
        {inner}
      </Link>
    );
  };

  return (
    <aside
      className="ipo-side fixed left-0 top-0 h-screen w-64 bg-white border-r border-gray-100 flex flex-col z-20"
      data-testid="sidebar"
    >
      {/* Logo */}
      <div className="pl-[6px] pr-0 py-0 -mt-[63px] -mb-[88px] border-b border-gray-100 flex items-center">
        <img
          src="/ipolabs-logo-full.png"
          alt="IPOLabs"
          className="h-[217px] w-auto object-contain -mr-[2px]"
          data-testid="sidebar-logo"
        />
      </div>

      {/* Navigation — Remote-style grouped sections */}
      <nav className="flex-1 overflow-y-auto px-3 py-4">
        {sections.map((sec, idx) => (
          <div key={sec.id} className={`space-y-0.5 ${idx === 0 ? "" : "mt-6"}`}>
            {sec.label && (
              <div className="ipo-side-label">
                {sec.label}
              </div>
            )}
            {sec.items.map(renderNavItem)}
          </div>
        ))}
      </nav>

      {/* Standalone Log Out button (always visible) */}
      <div className="px-3 pt-2">
        <button
          type="button"
          onClick={handleLogout}
          disabled={loggingOut}
          className="ipo-side-logout disabled:opacity-60"
          data-testid="sidebar-logout-btn"
        >
          <LogOut className="w-4 h-4 flex-shrink-0" strokeWidth={2} />
          <span className="flex-1 text-left">{loggingOut ? "Logging out…" : "Log Out"}</span>
        </button>
      </div>

      {/* User Section */}
      <div className="p-3 border-t border-gray-100">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="w-full flex items-center gap-2.5 px-2 py-2 rounded-md hover:bg-gray-50 transition-colors"
              data-testid="user-menu-trigger"
            >
              <Avatar className="h-7 w-7">
                <AvatarImage src={user?.picture} alt={user?.name} />
                <AvatarFallback className="bg-[#E1F5EE] text-[#0F6E56] text-xs">
                  {user?.name?.charAt(0) || "U"}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 text-left min-w-0">
                <p className="text-[12.5px] font-semibold text-gray-900 truncate leading-tight">
                  {user?.name || "User"}
                </p>
                <p className="text-[10.5px] text-gray-400 truncate leading-tight">{user?.email}</p>
              </div>
              <ChevronDown className="w-3.5 h-3.5 text-gray-300" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            {isAccountOwner && (
              <DropdownMenuItem asChild>
                <Link to="/account" className="flex items-center cursor-pointer" data-testid="account-settings-link">
                  <User className="w-4 h-4 mr-2" />
                  Account Details
                </Link>
              </DropdownMenuItem>
            )}
            {isAdmin && (
              <DropdownMenuItem asChild>
                <Link to="/admin" className="flex items-center cursor-pointer" data-testid="admin-center-dropdown-link">
                  <Shield className="w-4 h-4 mr-2" />
                  Admin Center
                </Link>
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="cursor-pointer text-red-600 focus:text-red-600"
              onSelect={handleLogout}
              disabled={loggingOut}
              data-testid="logout-btn"
            >
              {loggingOut ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Logging out...</>
              ) : (
                <><LogOut className="w-4 h-4 mr-2" />Log out</>
              )}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* ── Save & Logout confirmation dialog ────────────────────────── */}
      <Dialog open={confirmOpen} onOpenChange={(o) => !saving && !loggingOut && setConfirmOpen(o)}>
        <DialogContent className="max-w-sm" data-testid="logout-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              Log out of IPOLabs?
            </DialogTitle>
            <DialogDescription className="text-xs leading-relaxed">
              You'll be returned to the login screen. Want to save your in-progress work
              first? We'll flush any open editors and forms before signing you out.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setConfirmOpen(false)}
              disabled={saving || loggingOut}
              className="flex-1 text-xs h-9"
              data-testid="logout-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={performLogout}
              disabled={saving || loggingOut}
              className="flex-1 text-xs h-9 border-red-200 text-red-600 hover:bg-red-50"
              data-testid="logout-discard-btn"
            >
              {loggingOut ? <Loader2 className="w-3 h-3 animate-spin" /> : <>Discard &amp; log out</>}
            </Button>
            <Button
              onClick={saveAndLogout}
              disabled={saving || loggingOut}
              className="flex-1 text-xs h-9 bg-[#185FA5] hover:bg-[#124b84] text-white gap-1.5"
              data-testid="logout-save-btn"
            >
              {saving ? <><Loader2 className="w-3 h-3 animate-spin" /> Saving…</> : <><Save className="w-3 h-3" /> Save &amp; log out</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </aside>
  );
};

export default Sidebar;
